<template>
  <v-container class="identity-page px-6 px-md-10 py-8" :class="{ 'us-report-page': showUsReport }">
    <div class="query-flow">
      <div v-if="!showUsReport" class="flow-stepper mb-8">
        <div
          v-for="step in 3"
          :key="step"
          class="step-item"
          :class="{ active: queryStep >= step, completed: queryStep > step }"
        >
          <span>{{ step }}</span>
        </div>
      </div>

      <p v-if="!showUsReport" class="step-label mb-10">Paso {{ queryStep }} de 3: {{ currentStepLabel }}</p>

      <section v-if="queryStep === 1" class="flow-type-section">
        <h2>Selecciona el tipo de consulta</h2>
        <p>Elige si deseas validar la identidad de una persona o de una empresa</p>

        <div class="type-grid">
          <button
            type="button"
            class="type-card"
            :class="{ selected: queryType === 'person' }"
            @click="selectQueryType('person')"
          >
            <div class="type-icon">
              <v-icon :color="queryType === 'person' ? '#1876d2' : '#7a8699'" size="48">
                mdi-account-outline
              </v-icon>
            </div>
            <strong>Persona Física</strong>
            <span>Valida identidad individual, documentos y antecedentes</span>
          </button>

          <button
            type="button"
            class="type-card"
            :class="{ selected: queryType === 'company' }"
            @click="selectQueryType('company')"
          >
            <div class="type-icon">
              <v-icon :color="queryType === 'company' ? '#1876d2' : '#7a8699'" size="48">
                mdi-office-building-outline
              </v-icon>
            </div>
            <strong>Empresa</strong>
            <span>Evalúa riesgos corporativos, cumplimiento y antecedentes</span>
          </button>
        </div>

        <div class="flow-footer">
          <v-btn outlined class="flow-secondary-button" @click="handleCancel">
            Cancelar
          </v-btn>
          <v-btn
            depressed
            class="flow-primary-button"
            :disabled="!queryType"
            @click="queryStep = 2"
          >
            Continuar
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </section>

      <section
        v-else-if="queryStep === 2 && queryType === 'company' && !companyCountry"
        class="flow-type-section"
      >
        <h2>Selecciona el país de la empresa</h2>
        <p>Elige las fuentes que se utilizarán para realizar la consulta</p>

        <div class="type-grid">
          <button type="button" class="type-card" @click="selectCompanyCountry('MX')">
            <div class="type-icon">
              <v-icon size="48" color="#9CA3AF">mdi-map-marker-outline</v-icon>
            </div>
            <strong>Empresa mexicana</strong>
            <span>Consulta fiscal, corporativa y de cumplimiento en México</span>
          </button>

          <button type="button" class="type-card" @click="selectCompanyCountry('US')">
            <div class="type-icon">
              <v-icon size="48" color="#9CA3AF">mdi-domain</v-icon>
            </div>
            <strong>Empresa estadounidense</strong>
            <span>Verificación empresarial y búsqueda de riesgos en Estados Unidos</span>
          </button>
        </div>

        <div class="flow-footer">
          <v-btn outlined class="flow-secondary-button" @click="queryStep = 1">
            <v-icon left small>mdi-arrow-left</v-icon>
            Atrás
          </v-btn>
        </div>
      </section>

      <section v-else-if="queryStep === 2">
        <v-form ref="form">
          <v-card class="form-card pa-6 pa-md-10" elevation="2">
            <button
              v-if="queryType === 'company'"
              type="button"
              class="text-action mb-5"
              @click="resetCompanyCountry"
            >
              <v-icon left small color="#1876d2">mdi-arrow-left</v-icon>
              Cambiar país
            </button>

            <template v-if="queryType !== 'company' || companyCountry === 'MX'">
              <h2>{{ queryType === 'company' ? 'Datos de la empresa' : 'Datos de la persona' }}</h2>
              <p>Ingresa la información necesaria para realizar la consulta</p>
            </template>
            <template v-else>
              <h2>{{ usFlowTitle }}</h2>
              <p>{{ usFlowDescription }}</p>
            </template>

            <v-alert v-if="error" type="error" outlined dense class="mb-6">{{ error }}</v-alert>

            <div v-if="queryType === 'person'" class="form-grid">
              <label>
                CURP (opcional)
                <input v-model="personForm.curp" type="text" placeholder="XXXX000000XXXXXX00">
              </label>
              <label>
                Nombre(s) *
                <input v-model="personForm.nombres" type="text" placeholder="Juan Carlos" required>
              </label>
              <label>
                Apellido Paterno *
                <input v-model="personForm.apellidoPaterno" type="text" placeholder="Pérez" required>
              </label>
              <label>
                Apellido Materno (opcional)
                <input v-model="personForm.apellidoMaterno" type="text" placeholder="García">
              </label>
              <label>
                Fecha de Nacimiento *
                <input v-model="personForm.fechaNacimiento" type="date" required>
              </label>
              <label>
                Sexo (opcional)
                <select v-model="personForm.sexo">
                  <option value="">Seleccionar</option>
                  <option value="HOMBRE">Hombre</option>
                  <option value="MUJER">Mujer</option>
                </select>
              </label>
              <label>
                Estado de nacimiento (opcional)
                <select v-model="personForm.estadoNacimiento">
                  <option value="">Seleccionar</option>
                  <option v-for="estado in estados" :key="estado" :value="estado">{{ estado }}</option>
                </select>
              </label>
              <label>
                RFC (opcional)
                <input v-model="personForm.rfc" type="text" placeholder="XXXX000000XXX">
              </label>
              <label>
                Teléfono (opcional)
                <input v-model="personForm.telefono" type="text" placeholder="5512345678">
              </label>
              <label>
                Correo electrónico (opcional)
                <input v-model="personForm.correoElectronico" type="email" placeholder="correo@ejemplo.com">
              </label>

              <div class="documents-box">
                <strong>INE — frente y reverso *</strong>
                <p>Necesarios para validar identidad digital. Ambos archivos son obligatorios.</p>
                <div class="document-actions">
                  <v-file-input
                    v-model="personForm.frenteIne"
                    label="Frente del INE *"
                    outlined
                    dense
                    hide-details
                    prepend-icon=""
                    prepend-inner-icon="mdi-cloud-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    show-size
                  />
                  <v-file-input
                    v-model="personForm.reversoIne"
                    label="Reverso del INE *"
                    outlined
                    dense
                    hide-details
                    prepend-icon=""
                    prepend-inner-icon="mdi-cloud-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                    show-size
                  />
                </div>
              </div>
            </div>

            <div v-else-if="companyCountry === 'MX'" class="form-grid">
              <label>
                Razón Social *
                <input v-model="companyForm.razonSocial" type="text" placeholder="Empresa S.A. de C.V." required>
              </label>
              <label>
                RFC (opcional)
                <input v-model="companyForm.rfc" type="text" placeholder="ABC123456XXX">
              </label>
              <label>
                Nombre comercial o Marca (opcional)
                <input v-model="companyForm.marca" type="text" placeholder="Nombre con el que opera">
              </label>
              <label>
                Dominio de internet (opcional)
                <input v-model="companyForm.dominio" type="text" placeholder="empresa.com">
              </label>

              <div class="documents-box full-width">
                <strong>Constancia de Situación Fiscal (opcional)</strong>
                <p>Mejora la precisión del análisis fiscal del reporte.</p>
                <v-file-input
                  v-model="companyForm.constanciaFiscal"
                  label="Constancia fiscal"
                  outlined
                  dense
                  hide-details
                  prepend-icon=""
                  prepend-inner-icon="mdi-cloud-upload"
                  accept=".pdf,.jpg,.jpeg,.png"
                  show-size
                />
              </div>
            </div>

            <div v-else-if="usFlowStep === 'packages'" class="us-flow">
              <div v-for="group in usPackageGroups" :key="group.title" class="package-group">
                <h3>{{ group.title }}</h3>
                <label
                  v-for="item in group.items"
                  :key="item.value"
                  class="package-option"
                  :class="{ selected: isUsPackageSelected(item.value) }"
                >
                  <input
                    v-model="selectedUsPackages"
                    type="checkbox"
                    :value="item.value"
                    :disabled="item.required"
                  >
                  <span>
                    <strong>{{ item.label }}</strong>
                    <small>{{ item.description }}</small>
                  </span>
                </label>
              </div>
            </div>

            <div v-else-if="usFlowStep === 'business'" class="us-flow form-grid">
              <label class="full-width">
                Nombre de la empresa *
                <input v-model.trim="usCompanyForm.businessName" type="text" placeholder="Nombre legal de la empresa">
              </label>
              <label class="full-width">
                Dirección principal *
                <input v-model.trim="usCompanyForm.officeAddress" type="text" placeholder="Calle, ciudad, estado y código postal">
              </label>
              <label>
                DBA o nombre comercial
                <input v-model.trim="usCompanyForm.dbaName" type="text" placeholder="Nombre con el que opera">
              </label>
              <label>
                TIN/EIN *
                <input v-model.trim="usCompanyForm.tinEin" type="text" maxlength="10" inputmode="numeric" placeholder="XX-XXXXXXX">
              </label>
              <label>
                Estado de constitución *
                <select v-model="usCompanyForm.formationState">
                  <option value="">Seleccionar</option>
                  <option v-for="state in usStates" :key="state.value" :value="state.value">
                    {{ state.text }}
                  </option>
                </select>
              </label>
              <label>
                Sitio web
                <input v-model.trim="usCompanyForm.website" type="text" placeholder="www.empresa.com">
              </label>
              <label class="full-width">
                Correo electrónico
                <input v-model.trim="usCompanyForm.email" type="email" placeholder="contacto@empresa.com">
              </label>
            </div>

            <div v-else-if="usFlowStep === 'people'" class="us-flow">
              <div v-for="(person, index) in usPeople" :key="person.id" class="associated-person">
                <div class="associated-person-header">
                  <h3>Persona {{ index + 1 }}</h3>
                  <v-btn v-if="usPeople.length" text color="#1876d2" @click="removeUsPerson(index)">
                    Eliminar
                  </v-btn>
                </div>
                <div class="form-grid">
                  <label>Nombre *<input v-model.trim="person.firstName" type="text" placeholder="Nombre"></label>
                  <label>Segundo nombre<input v-model.trim="person.middleName" type="text" placeholder="Segundo nombre"></label>
                  <label>Apellido *<input v-model.trim="person.lastName" type="text" placeholder="Apellido"></label>
                  <label>Sufijo<input v-model.trim="person.suffix" type="text" placeholder="Jr, Sr, III"></label>
                  <label>SSN completo<input v-model.trim="person.ssn" type="text" maxlength="11" inputmode="numeric" placeholder="XXX-XX-XXXX"></label>
                  <label>Fecha de nacimiento<input v-model="person.dateOfBirth" type="date"></label>
                  <label class="full-width">Teléfono<input v-model.trim="person.phoneNumber" type="tel" placeholder="+12125550125"></label>
                </div>
              </div>
              <v-btn text color="#1876d2" class="px-0" @click="addUsPerson">
                <v-icon left>mdi-account-plus-outline</v-icon>
                Agregar otra persona
              </v-btn>
            </div>

            <div v-else class="us-flow">
              <div class="review-card">
                <h3>Datos del negocio</h3>
                <div class="confirmation-grid">
                  <div class="confirmation-field full-width">
                    <span>Nombre legal</span>
                    <strong>{{ usCompanyForm.businessName }}</strong>
                  </div>
                  <div class="confirmation-field full-width">
                    <span>Dirección principal</span>
                    <strong>{{ usCompanyForm.officeAddress }}</strong>
                  </div>
                  <div class="confirmation-field">
                    <span>DBA o nombre comercial</span>
                    <strong>{{ usCompanyForm.dbaName || 'No proporcionado' }}</strong>
                  </div>
                  <div class="confirmation-field">
                    <span>EIN/TIN</span>
                    <strong>{{ formatTinForDisplay(usCompanyForm.tinEin) }}</strong>
                  </div>
                  <div class="confirmation-field">
                    <span>Estado de constitución</span>
                    <strong>{{ getUsStateName(usCompanyForm.formationState) }}</strong>
                  </div>
                  <div class="confirmation-field">
                    <span>Sitio web</span>
                    <strong>{{ usCompanyForm.website || 'No proporcionado' }}</strong>
                  </div>
                  <div class="confirmation-field full-width">
                    <span>Correo electrónico</span>
                    <strong>{{ usCompanyForm.email || 'No proporcionado' }}</strong>
                  </div>
                </div>
              </div>
              <div class="review-card">
                <h3>Servicios seleccionados</h3>
                <v-chip v-for="item in selectedUsPackageDetails" :key="item.value" small outlined class="mr-2 mb-2">
                  {{ item.label }}
                </v-chip>
              </div>
              <div class="review-card">
                <h3>Personas relacionadas</h3>
                <p v-if="!usPeople.length" class="confirmation-empty">No se agregaron personas relacionadas.</p>
                <div v-else class="confirmation-people">
                  <div v-for="(person, index) in usPeople" :key="person.id" class="confirmation-person">
                    <strong>{{ getUsPersonName(person) || `Persona ${index + 1}` }}</strong>
                    <div class="confirmation-grid compact">
                      <div class="confirmation-field">
                        <span>Fecha de nacimiento</span>
                        <strong>{{ person.dateOfBirth || 'No proporcionada' }}</strong>
                      </div>
                      <div class="confirmation-field">
                        <span>SSN</span>
                        <strong>{{ maskSsn(person.ssn) }}</strong>
                      </div>
                      <div class="confirmation-field full-width">
                        <span>Teléfono</span>
                        <strong>{{ person.phoneNumber || 'No proporcionado' }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-footer">
              <v-btn outlined class="flow-secondary-button" @click="handleFormBack">
                <v-icon left small>mdi-arrow-left</v-icon>
                Atrás
              </v-btn>
              <v-btn depressed class="flow-primary-button" :loading="loading" :disabled="loading" @click="handleFormContinue">
                <v-icon v-if="showAnalysisIcon" left small>mdi-magnify</v-icon>
                {{ formContinueLabel }}
              </v-btn>
            </div>
          </v-card>
        </v-form>
      </section>

      <section v-else>
        <v-card v-if="error && !showUsReport" class="processing-card pa-10" elevation="2">
          <div class="processing-icon error-state-icon">
            <v-icon color="white" size="42">mdi-alert-outline</v-icon>
          </div>
          <h2>No se pudo cargar el reporte</h2>
          <p>{{ error }}</p>
          <v-btn depressed class="flow-primary-button mt-6" @click="retryMiddeskBusiness">
            <v-icon left small>mdi-refresh</v-icon>
            Reintentar
          </v-btn>
        </v-card>

        <v-card v-else-if="!showUsReport" class="processing-card pa-10" elevation="2">
          <div class="processing-icon">
            <v-progress-circular indeterminate color="white" size="40" width="4" />
          </div>
          <h2>Analizando información...</h2>
          <p>{{ processingMessage }}</p>
          <v-btn
            v-if="middeskConsultationId && !loading"
            outlined
            class="mt-6"
            @click="getMiddeskConsultation(middeskConsultationId, true)"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Actualizar estado
          </v-btn>
        </v-card>

        <div v-else class="us-report">
          <v-alert type="info" outlined class="mb-6">
            {{ middeskBusiness
              ? 'Reporte obtenido de Middesk Sandbox. Los resultados son datos de prueba del proveedor.'
              : 'Reporte de demostración. Los resultados mostrados no provienen del proveedor.' }}
          </v-alert>

          <div class="us-report-heading">
            <div>
              <span class="report-eyebrow">Verificación empresarial · Estados Unidos</span>
              <h1>{{ usCompanyForm.businessName }}</h1>
              <p>Consulta generada el {{ usMockGeneratedAt }}</p>
            </div>
            <div class="us-report-actions">
              <v-chip :color="usReportStatusMeta.color" :text-color="usReportStatusMeta.textColor">
                {{ usReportStatusMeta.label }}
              </v-chip>
            </div>
          </div>

          <div class="us-report-tabs">
            <button
              v-for="tab in usReportTabs"
              :key="tab.value"
              type="button"
              :class="{ active: activeUsReportTab === tab.value }"
              @click="activeUsReportTab = tab.value"
            >
              {{ tab.label }}
            </button>
          </div>

          <div v-if="activeUsReportTab === 'verification'" class="us-report-layout">
            <aside class="us-summary-panel">
              <h2>Resumen</h2>
              <div v-for="(item, index) in usMockSummary" :key="`${item.label}-${index}`" class="us-summary-row">
                <span>{{ item.label }}</span>
                <strong :class="`us-summary-state--${item.status}`">
                  <v-icon small :color="item.iconColor">{{ item.icon }}</v-icon>
                  {{ item.value }}
                </strong>
                <details v-if="item.message" class="us-summary-explanation">
                  <summary>Ver detalle</summary>
                  <p>{{ item.message }}</p>
                </details>
              </div>
            </aside>

            <main class="us-report-content">
              <section class="provider-card us-business-card">
                <div class="provider-card-header">
                  <h2>Detalles de la empresa</h2>
                  <v-btn
                    small
                    outlined
                    rounded
                    :loading="downloadingPdf"
                    :disabled="!middeskConsultationId || downloadingPdf"
                    @click="downloadMiddeskPdf"
                  >
                    Descargar PDF
                  </v-btn>
                </div>
                <div class="business-detail-grid">
                  <div>
                    <span>Nombre empresarial</span>
                    <strong>{{ usCompanyForm.businessName }}</strong>
                    <small class="us-detail-status">
                      <v-icon size="16" :color="usNameReviewMeta.color">{{ usNameReviewMeta.cssClass === 'status-success' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                      {{ usNameReviewMeta.label }}
                    </small>
                  </div>
                  <div>
                    <span>Dirección principal</span>
                    <strong>{{ usCompanyForm.officeAddress }}</strong>
                    <small class="us-detail-status">
                      <v-icon size="16" :color="usAddressReviewMeta.color">{{ usAddressReviewMeta.cssClass === 'status-success' ? 'mdi-check-circle-outline' : usAddressReviewMeta.cssClass === 'status-error' ? 'mdi-close-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
                      {{ usAddressReviewMeta.label }}
                    </small>
                  </div>
                  <div>
                    <span>Estado de constitución</span>
                    <strong>{{ getUsStateName(usCompanyForm.formationState) }}</strong>
                  </div>
                  <div>
                    <span>Tipo de entidad</span>
                    <strong>{{ usEntityType }}</strong>
                  </div>
                  <div>
                    <span>Fecha de constitución</span>
                    <strong>{{ usFormationDate }}</strong>
                  </div>
                </div>
              </section>

              <section v-for="section in usMockReportSections" :key="section.title" class="provider-card">
                <div class="provider-card-header">
                  <h2>{{ section.title }}</h2>
                  <div>
                    <v-chip small outlined :color="section.color">{{ section.status }}</v-chip>
                    <small class="last-updated">Actualizado {{ usMockGeneratedAt }}</small>
                  </div>
                </div>
                <div class="provider-table">
                  <div v-for="(row, rowIndex) in section.rows" :key="row.key || `${section.title}-${row.label}-${rowIndex}`" class="provider-table-row">
                    <span>{{ row.label }}</span>
                    <strong>{{ row.value }}</strong>
                    <small v-if="row.note">{{ row.note }}</small>
                    <div v-if="row.details && row.details.length" class="provider-row-details">
                      <span v-for="detail in row.details" :key="detail">{{ detail }}</span>
                    </div>
                    <a
                      v-if="row.url"
                      :href="row.url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >Consultar fuente</a>
                  </div>
                </div>
              </section>
            </main>
          </div>

          <div v-else-if="activeUsReportTab === 'sources'" class="provider-card">
            <div class="provider-card-header">
              <div>
                <h2>Fuentes consultadas</h2>
                <p>{{ middeskBusiness ? 'Fuentes reportadas por Middesk.' : 'Fuentes simuladas para validar la estructura del reporte.' }}</p>
              </div>
              <v-chip small color="#EEF0F4">{{ usMockSources.length }} fuentes</v-chip>
            </div>
            <div class="provider-table">
              <div v-for="source in usMockSources" :key="source.key || source.name" class="source-row">
                <div>
                  <strong>{{ source.name }}</strong>
                  <span>{{ source.description }}</span>
                  <a
                    v-if="source.url"
                    :href="source.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >Abrir fuente</a>
                </div>
                <v-chip small outlined :color="source.color">{{ source.status }}</v-chip>
              </div>
            </div>
          </div>

          <div v-else class="provider-card">
            <div class="provider-card-header">
              <div>
                <h2>Respuesta API</h2>
                <p>Vista previa del contrato que podría entregar el backend estadounidense.</p>
              </div>
              <v-chip small color="#FFF3CD" text-color="#8A6D3B">
                {{ middeskBusiness ? 'Sandbox' : 'Mock' }}
              </v-chip>
            </div>
            <pre class="api-response">{{ formattedUsMockApiResponse }}</pre>
          </div>

          <div class="form-footer">
            <v-btn outlined class="flow-secondary-button" @click="handleCancel">Volver al historial</v-btn>
            <v-btn depressed class="flow-primary-button" @click="startAnotherQuery">Nueva consulta</v-btn>
          </div>
        </div>
      </section>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'DueDiligenceNew',
  data() {
    return {
      loading: false,
      downloadingPdf: false,
      error: null,
      queryStep: 1,
      queryType: 'person',
      companyCountry: '',
      usFlowStep: 'packages',
      selectedUsPackages: ['verify'],
      usMockStatus: 'idle',
      showUsReport: false,
      activeUsReportTab: 'verification',
      middeskConsultationId: '',
      middeskConsultation: null,
      usIdempotencyKey: '',
      middeskBusinessId: '',
      middeskBusiness: null,
      personForm: {
        curp: '',
        nombres: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        sexo: '',
        estadoNacimiento: '',
        fechaNacimiento: '',
        telefono: '',
        rfc: '',
        correoElectronico: '',
        frenteIne: null,
        reversoIne: null,
      },
      companyForm: {
        razonSocial: '',
        rfc: '',
        marca: '',
        dominio: '',
        constanciaFiscal: null,
      },
      usCompanyForm: {
        businessName: '',
        officeAddress: '',
        dbaName: '',
        tinEin: '',
        formationState: '',
        website: '',
        email: '',
      },
      usPeople: [],
      usPackageGroups: [
        {
          title: 'Verificación empresarial',
          items: [
            {
              value: 'verify',
              label: 'Verificación de empresa',
              description: 'Valida identidad empresarial, TIN y registros estatales.',
              required: true,
            },
          ],
        },
        {
          title: 'Cumplimiento y riesgo',
          items: [
            {
              value: 'risk_analysis',
              label: 'Análisis de riesgo',
              description: 'Analiza la presencia web y señales de riesgo.',
            },
            {
              value: 'adverse_media',
              label: 'Medios adversos',
              description: 'Busca noticias adversas relacionadas con la empresa.',
            },
            {
              value: 'enhanced_screenings',
              label: 'Screening ampliado',
              description: 'Busca watchlists y personas políticamente expuestas.',
            },
          ],
        },
        {
          title: 'Evaluación crediticia',
          items: [
            {
              value: 'ucc_tax',
              label: 'UCC e impuestos federales',
              description: 'Consulta gravámenes e impuestos federales.',
            },
            {
              value: 'business_litigation',
              label: 'Litigios empresariales',
              description: 'Consulta litigios asociados con la empresa.',
            },
            {
              value: 'business_bankruptcy',
              label: 'Bancarrotas empresariales',
              description: 'Consulta registros de bancarrota.',
            },
          ],
        },
        {
          title: 'Evaluación de personas relacionadas',
          items: [
            {
              value: 'people_litigation',
              label: 'Litigios personales',
              description: 'Consulta litigios de las personas relacionadas.',
            },
            {
              value: 'people_bankruptcy',
              label: 'Bancarrotas personales',
              description: 'Consulta bancarrotas usando nombre y SSN completo.',
            },
            {
              value: 'people_tax_liens',
              label: 'Gravámenes fiscales personales',
              description: 'Consulta gravámenes fiscales de las personas relacionadas.',
            },
            {
              value: 'people_ucc_liens',
              label: 'Gravámenes UCC personales',
              description: 'Consulta gravámenes UCC de las personas relacionadas.',
            },
          ],
        },
      ],
      usStates: [
        { text: 'Alabama', value: 'AL' }, { text: 'Alaska', value: 'AK' },
        { text: 'Arizona', value: 'AZ' }, { text: 'Arkansas', value: 'AR' },
        { text: 'California', value: 'CA' }, { text: 'Colorado', value: 'CO' },
        { text: 'Connecticut', value: 'CT' }, { text: 'Delaware', value: 'DE' },
        { text: 'District of Columbia', value: 'DC' }, { text: 'Florida', value: 'FL' },
        { text: 'Georgia', value: 'GA' }, { text: 'Hawaii', value: 'HI' },
        { text: 'Idaho', value: 'ID' }, { text: 'Illinois', value: 'IL' },
        { text: 'Indiana', value: 'IN' }, { text: 'Iowa', value: 'IA' },
        { text: 'Kansas', value: 'KS' }, { text: 'Kentucky', value: 'KY' },
        { text: 'Louisiana', value: 'LA' }, { text: 'Maine', value: 'ME' },
        { text: 'Maryland', value: 'MD' }, { text: 'Massachusetts', value: 'MA' },
        { text: 'Michigan', value: 'MI' }, { text: 'Minnesota', value: 'MN' },
        { text: 'Mississippi', value: 'MS' }, { text: 'Missouri', value: 'MO' },
        { text: 'Montana', value: 'MT' }, { text: 'Nebraska', value: 'NE' },
        { text: 'Nevada', value: 'NV' }, { text: 'New Hampshire', value: 'NH' },
        { text: 'New Jersey', value: 'NJ' }, { text: 'New Mexico', value: 'NM' },
        { text: 'New York', value: 'NY' }, { text: 'North Carolina', value: 'NC' },
        { text: 'North Dakota', value: 'ND' }, { text: 'Ohio', value: 'OH' },
        { text: 'Oklahoma', value: 'OK' }, { text: 'Oregon', value: 'OR' },
        { text: 'Pennsylvania', value: 'PA' }, { text: 'Rhode Island', value: 'RI' },
        { text: 'South Carolina', value: 'SC' }, { text: 'South Dakota', value: 'SD' },
        { text: 'Tennessee', value: 'TN' }, { text: 'Texas', value: 'TX' },
        { text: 'Utah', value: 'UT' }, { text: 'Vermont', value: 'VT' },
        { text: 'Virginia', value: 'VA' }, { text: 'Washington', value: 'WA' },
        { text: 'West Virginia', value: 'WV' }, { text: 'Wisconsin', value: 'WI' },
        { text: 'Wyoming', value: 'WY' },
      ],
      estados: [
        'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
        'Chiapas', 'Chihuahua', 'CDMX', 'Coahuila', 'Colima', 'Durango',
        'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco',
        'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla',
        'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora',
        'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas',
      ],
    };
  },
  computed: {
    currentStepLabel() {
      if (this.queryStep === 1) return 'Tipo de consulta';
      if (this.queryStep === 2) return 'Datos';
      return this.showUsReport ? 'Resultado' : 'Procesando';
    },
    usFlowTitle() {
      const titles = {
        packages: 'Configura la consulta',
        business: 'Información de la empresa',
        people: 'Personas relacionadas',
        confirmation: 'Confirma la consulta',
      };
      return titles[this.usFlowStep];
    },
    usFlowDescription() {
      const descriptions = {
        packages: 'Selecciona los análisis que deseas incluir en el reporte.',
        business: 'Ingresa los datos necesarios para realizar la búsqueda.',
        people: 'Agrega propietarios, representantes o personas asociadas.',
        confirmation: 'Revisa la información antes de iniciar el análisis.',
      };
      return descriptions[this.usFlowStep];
    },
    selectedUsPackageDetails() {
      return this.usPackageGroups
        .reduce((items, group) => items.concat(group.items), [])
        .filter((item) => this.selectedUsPackages.includes(item.value));
    },
    formContinueLabel() {
      if (this.queryType !== 'company' || this.companyCountry === 'MX') return 'Iniciar análisis';
      if (this.usFlowStep === 'people') return 'Revisar consulta';
      if (this.usFlowStep === 'confirmation') return 'Iniciar análisis';
      return 'Continuar';
    },
    showAnalysisIcon() {
      return this.queryType !== 'company'
        || this.companyCountry === 'MX'
        || this.usFlowStep === 'confirmation';
    },
    processingMessage() {
      return this.companyCountry === 'US'
        ? 'Estamos obteniendo la información de Middesk y preparando el reporte.'
        : 'Nuestros agentes de IA están validando la información y generando el reporte';
    },
    usReportStatusMeta() {
      return this.getMiddeskBusinessStatusMeta(this.middeskBusiness?.status);
    },
    usNameReviewMeta() {
      return this.getMiddeskTaskMeta(['name']);
    },
    usAddressReviewMeta() {
      return this.getMiddeskTaskMeta(['address', 'address_verification']);
    },
    usEntityType() {
      return this.middeskBusiness?.formation?.entity_type || 'No disponible';
    },
    usFormationDate() {
      return this.middeskBusiness?.formation?.formation_date || 'No disponible';
    },
    usMockSummary() {
      if (this.middeskBusiness) return this.buildMiddeskSummary();

      return [
        {
          label: 'Nombre empresarial', value: 'Coincidencia similar', status: 'warning',
          icon: 'mdi-alert-circle-outline', iconColor: '#D97706',
        },
        {
          label: 'Dirección', value: 'Verificada', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
        {
          label: 'Riesgo de dirección', value: 'Bajo', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
        {
          label: 'Registros estatales', value: 'Activo', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
        {
          label: 'Watchlists', value: 'Sin coincidencias', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
        {
          label: 'Bancarrotas', value: 'No encontradas', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
        {
          label: 'Conexiones', value: 'No encontradas', status: 'success',
          icon: 'mdi-check-circle-outline', iconColor: '#00A342',
        },
      ];
    },
    usMockGeneratedAt() {
      const reportDate = this.middeskBusiness?.updated_at || this.middeskBusiness?.created_at;
      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      }).format(reportDate ? new Date(reportDate) : new Date());
    },
    usReportTabs() {
      return [
        { label: 'Verificación empresarial', value: 'verification' },
        { label: 'Fuentes', value: 'sources' },
        { label: 'Respuesta API', value: 'api' },
      ];
    },
    usMockSources() {
      if (this.middeskBusiness) {
        const sources = this.buildMiddeskSources();
        return sources.length ? sources : [{
          name: 'Middesk',
          description: 'La respuesta no incluyó el detalle de las fuentes consultadas.',
          status: 'Sin detalle',
          color: '#667085',
        }];
      }

      return [
        { name: 'Secretary of State', description: 'Registros estatales y estado de la entidad.', status: 'Completada', color: '#00A342' },
        { name: 'Address verification', description: 'Entregabilidad, uso comercial y frecuencia.', status: 'Completada', color: '#00A342' },
        { name: 'United States watchlists', description: 'Listas de sanciones y vigilancia.', status: 'Sin coincidencias', color: '#00A342' },
        { name: 'Court records', description: 'Litigios y registros de bancarrota.', status: 'Completada', color: '#00A342' },
      ];
    },
    formattedUsMockApiResponse() {
      if (this.middeskBusiness) {
        return JSON.stringify(this.sanitizeMiddeskResponse(this.middeskBusiness), null, 2);
      }

      return JSON.stringify({
        demo: true,
        status: 'completed',
        country: 'US',
        business: {
          name: this.usCompanyForm.businessName,
          address: this.usCompanyForm.officeAddress,
          formation_state: this.usCompanyForm.formationState,
          name_match: 'similar',
          address_status: 'verified',
        },
        packages: this.selectedUsPackages,
        people_count: this.usPeople.length,
        watchlists: { status: 'no_hits' },
      }, null, 2);
    },
    usMockReportSections() {
      if (this.middeskBusiness) return this.buildMiddeskReportSections();

      return [
        {
          title: 'Detalles de la empresa', status: 'Verificada', color: '#00A342',
          rows: [
            { label: 'Nombre presentado', value: this.usCompanyForm.businessName },
            { label: 'DBA', value: this.usCompanyForm.dbaName || 'No proporcionado' },
            { label: 'Estado de constitución', value: this.getUsStateName(this.usCompanyForm.formationState) },
          ],
        },
        {
          title: 'Direcciones', status: 'Riesgo bajo', color: '#00A342',
          rows: [
            { label: 'Dirección presentada', value: this.usCompanyForm.officeAddress },
            { label: 'Clasificación', value: 'Comercial y entregable' },
          ],
        },
        {
          title: 'Personas relacionadas', status: `${this.usPeople.length} encontradas`, color: '#2163FF',
          rows: this.usPeople.map((person) => ({
            label: [person.firstName, person.middleName, person.lastName, person.suffix].filter(Boolean).join(' '),
            value: 'Persona presentada',
          })),
        },
        {
          title: 'Watchlists y conexiones', status: 'Sin hallazgos', color: '#00A342',
          rows: [
            { label: 'Watchlists', value: 'Sin coincidencias' },
            { label: 'Conexiones de riesgo', value: 'No encontradas' },
          ],
        },
        {
          title: 'Gravámenes, litigios y bancarrotas', status: 'Sin hallazgos', color: '#00A342',
          rows: [
            { label: 'Gravámenes UCC', value: this.isUsPackageSelected('ucc_tax') ? 'Sin hallazgos' : 'No solicitado' },
            { label: 'Litigios empresariales', value: this.isUsPackageSelected('business_litigation') ? 'Sin hallazgos' : 'No solicitado' },
            { label: 'Bancarrotas', value: this.isUsPackageSelected('business_bankruptcy') ? 'Sin hallazgos' : 'No solicitado' },
          ],
        },
      ];
    },
    selectLanguage() {
      return this.$store.state.language;
    },
  },
  created() {
    if (this.$route.query.provider === 'middesk' && this.$route.query.consultationId) {
      this.getMiddeskConsultation(this.$route.query.consultationId, true);
      return;
    }

    if (this.$route.query.provider === 'middesk' && this.$route.query.businessId) {
      this.getMiddeskBusiness(this.$route.query.businessId);
      return;
    }

    if (this.$route.query.demo === 'us-report') {
      this.loadUsReportDemo();
    }
  },
  methods: {
    getAuthorizationHeaders() {
      return {
        Authorization: this.$store.state.sToken
          ? `Bearer ${this.$store.state.sToken}`
          : '',
      };
    },
    createIdempotencyKey() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }

      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
      });
    },
    applyMiddeskConsultation(consultation) {
      this.middeskConsultation = consultation;
      this.middeskConsultationId = consultation.id;
      this.middeskBusinessId = consultation.providerBusinessId || '';
      this.middeskBusiness = consultation.providerResponse || null;
      this.usMockStatus = consultation.providerStatus || consultation.status;

      if (this.middeskBusiness) {
        this.mapMiddeskBusinessToReport(this.middeskBusiness);
      } else {
        this.usCompanyForm.businessName = consultation.businessName
          || this.usCompanyForm.businessName;
      }

      this.activeUsReportTab = 'verification';
      this.showUsReport = consultation.status === 'report_available'
        && Boolean(this.middeskBusiness);

      if (consultation.status === 'failed') {
        this.error = consultation.lastError?.message
          || 'La consulta no pudo ser procesada por Middesk';
      }
    },
    async getMiddeskConsultation(consultationId, synchronize = false) {
      const hasCachedReport = this.middeskConsultationId === consultationId
        && Boolean(this.middeskBusiness);
      this.queryType = 'company';
      this.companyCountry = 'US';
      this.queryStep = 3;
      this.middeskConsultationId = consultationId;
      if (!hasCachedReport) {
        this.middeskConsultation = null;
        this.middeskBusiness = null;
        this.showUsReport = false;
      }
      this.loading = true;
      this.error = null;

      try {
        const url = `${URI}/api/v1/${this.selectLanguage}/middesk/consultations/${consultationId}`;
        const response = synchronize
          ? await DB.post(`${url}/sync`, {}, { headers: this.getAuthorizationHeaders() })
          : await DB.get(url, { headers: this.getAuthorizationHeaders() });

        this.applyMiddeskConsultation(response.data.data);
      } catch (error) {
        this.error = error.response?.data?.message
          || 'No se pudo actualizar la consulta de Middesk';
        this.showUsReport = hasCachedReport;
      } finally {
        this.loading = false;
      }
    },
    async getMiddeskBusiness(businessId) {
      this.queryType = 'company';
      this.companyCountry = 'US';
      this.queryStep = 3;
      this.middeskBusinessId = businessId;
      this.middeskBusiness = null;
      this.showUsReport = false;
      this.loading = true;
      this.error = null;

      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage}/middesk/businesses/${businessId}`,
          {
            headers: this.getAuthorizationHeaders(),
          }
        );

        this.middeskBusiness = response.data.data;
        this.mapMiddeskBusinessToReport(this.middeskBusiness);
        this.activeUsReportTab = 'verification';
        this.showUsReport = true;
      } catch (error) {
        this.error = error.response?.data?.message || 'No se pudo cargar la empresa de Middesk';
      } finally {
        this.loading = false;
      }
    },
    retryMiddeskBusiness() {
      if (this.middeskConsultationId) {
        this.getMiddeskConsultation(this.middeskConsultationId, true);
      } else if (this.middeskBusinessId) {
        this.getMiddeskBusiness(this.middeskBusinessId);
      }
    },
    async downloadMiddeskPdf() {
      if (!this.middeskConsultationId || this.downloadingPdf) return;

      this.downloadingPdf = true;
      this.error = null;

      try {
        const response = await DB.get(
          `${URI}/api/v1/${this.selectLanguage}/middesk/consultations/${this.middeskConsultationId}/pdf`,
          {
            headers: this.getAuthorizationHeaders(),
            responseType: 'blob',
          }
        );
        const blobUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
        );
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        const safeBusinessName = (this.usCompanyForm.businessName || 'reporte-middesk')
          .replace(/[^a-z0-9-_]+/gi, '-')
          .replace(/^-+|-+$/g, '');
        downloadLink.download = `${safeBusinessName || 'reporte-middesk'}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        this.error = error.response?.data?.message
          || 'El PDF todavía no está disponible para descarga';
      } finally {
        this.downloadingPdf = false;
      }
    },
    mapMiddeskBusinessToReport(business) {
      const addresses = Array.isArray(business.addresses) ? business.addresses : [];
      const names = Array.isArray(business.names) ? business.names : [];
      const people = Array.isArray(business.people) ? business.people : [];
      const orders = Array.isArray(business.orders) ? business.orders : [];
      const primaryAddress = addresses.find((address) => address.submitted) || addresses[0] || {};
      const dbaName = names.find((name) => !name.submitted)?.name || '';

      this.usCompanyForm = {
        businessName: business.name || 'Empresa sin nombre',
        officeAddress: this.formatMiddeskAddress(primaryAddress),
        dbaName,
        tinEin: '',
        formationState: business.formation?.formation_state || primaryAddress.state || '',
        website: business.website?.url || '',
        email: business.email_addresses?.find((email) => email.submitted)?.email || '',
      };

      this.usPeople = people.length
        ? people.map((person, index) => ({
          id: person.id || index + 1,
          firstName: person.first_name || person.name || 'Nombre no disponible',
          middleName: person.middle_name || '',
          lastName: person.last_name || '',
          suffix: person.suffix || '',
          ssn: '',
          dateOfBirth: person.dob || person.date_of_birth || '',
          phoneNumber: '',
        }))
        : [];

      const packageMap = {
        business_verification_verify: 'verify',
        identity: 'verify',
        website: 'risk_analysis',
        enhanced_screenings: 'enhanced_screenings',
        adverse_media: 'adverse_media',
        tax_liens: 'ucc_tax',
        ucc_liens: 'ucc_tax',
        liens: 'ucc_tax',
        litigations: 'business_litigation',
        bankruptcies: 'business_bankruptcy',
        people_litigations: 'people_litigation',
        people_bankruptcies: 'people_bankruptcy',
        people_tax_liens: 'people_tax_liens',
        people_ucc_liens: 'people_ucc_liens',
      };
      const selectedPackages = orders.map((order) => packageMap[order.product]).filter(Boolean);
      this.selectedUsPackages = Array.from(new Set(['verify', ...selectedPackages]));
      this.usMockStatus = business.status || 'completed';
    },
    formatMiddeskAddress(address) {
      if (!address) return 'No proporcionada';
      if (address.full_address) return address.full_address;
      return [
        address.address_line1,
        address.address_line2,
        address.city,
        address.state,
        address.postal_code,
      ].filter(Boolean).join(', ') || 'No proporcionada';
    },
    getMiddeskBusinessStatusMeta(status) {
      const statuses = {
        open: { label: 'Abierta', color: '#EEF0F4', textColor: '#465266' },
        pending: { label: 'En proceso', color: '#E8F0FF', textColor: '#2163FF' },
        in_audit: { label: 'En auditoría', color: '#E8F0FF', textColor: '#2163FF' },
        in_review: { label: 'Requiere revisión', color: '#FFF3CD', textColor: '#8A6D3B' },
        approved: { label: 'Aprobada', color: '#D9FAE4', textColor: '#008A38' },
        rejected: { label: 'Rechazada', color: '#FFE0E0', textColor: '#E60012' },
      };
      return statuses[status]
        || { label: status || 'Sin evaluar', color: '#EEF0F4', textColor: '#465266' };
    },
    getMiddeskTaskMeta(keys) {
      return this.getMiddeskReviewMeta(keys, 'Sin evaluar');
    },
    getMiddeskReviewTask(keys) {
      const tasks = this.middeskBusiness?.review?.tasks || [];
      return tasks.find((task) => keys.includes(task.key) || keys.includes(task.category) || keys.includes(task.name));
    },
    getMiddeskReviewMeta(keys, fallbackLabel) {
      const task = this.getMiddeskReviewTask(keys);
      if (!task) {
        return { label: fallbackLabel, color: '#667085', cssClass: '', status: 'neutral' };
      }
      const status = String(task.status || '').toLowerCase();
      if (status === 'success') {
        return {
          label: task.sub_label || 'Verificada', color: '#00A342',
          cssClass: 'status-success', status: 'success',
        };
      }
      if (status === 'failure' || status === 'failed') {
        return {
          label: task.sub_label || 'Con hallazgos', color: '#E60012',
          cssClass: 'status-error', status: 'error',
        };
      }
      return {
        label: task.sub_label || 'Revisión', color: '#D97706',
        cssClass: 'status-warning', status: 'warning',
      };
    },
    buildMiddeskSummary() {
      const tasks = this.middeskBusiness?.review?.tasks || [];
      if (tasks.length) {
        return tasks.map((task, index) => {
          const meta = this.getMiddeskReviewMeta([task.key], task.sub_label || 'Sin evaluar');
          return {
            label: task.label || task.category || `Validación ${index + 1}`,
            value: task.sub_label || meta.label,
            message: task.message || '',
            status: meta.status,
            icon: meta.status === 'success'
              ? 'mdi-check-circle-outline'
              : meta.status === 'error'
                ? 'mdi-close-circle-outline'
                : 'mdi-alert-circle-outline',
            iconColor: meta.color,
          };
        });
      }

      const definitions = [
        { label: 'Nombre empresarial', keys: ['name'], fallback: 'Sin evaluar' },
        { label: 'Dirección', keys: ['address', 'address_verification'], fallback: 'Sin evaluar' },
        { label: 'Registros estatales', keys: ['sos', 'registration', 'registrations'], fallback: 'Sin evaluar' },
        { label: 'Watchlists', keys: ['watchlist'], fallback: 'Sin evaluar' },
        { label: 'Bancarrotas', keys: ['bankruptcies', 'bankruptcy'], fallback: 'No solicitada' },
        { label: 'Conexiones', keys: ['business_connections'], fallback: 'No evaluadas' },
      ];

      return definitions.map((definition) => {
        const meta = this.getMiddeskReviewMeta(definition.keys, definition.fallback);
        return {
          label: definition.label,
          value: meta.label,
          status: meta.status,
          icon: meta.status === 'success'
            ? 'mdi-check-circle-outline'
            : meta.status === 'error'
              ? 'mdi-close-circle-outline'
              : 'mdi-alert-circle-outline',
          iconColor: meta.color,
        };
      });
    },
    buildMiddeskSources() {
      const business = this.middeskBusiness || {};
      const tasks = business.review?.tasks || [];
      const sourceOwners = [
        ...tasks,
        ...(business.names || []),
        ...(business.addresses || []),
        ...(business.people || []),
        ...(business.formation ? [business.formation] : []),
      ];
      const sources = sourceOwners.reduce((allSources, owner) => [
        ...allSources,
        ...(Array.isArray(owner.sources) ? owner.sources : []),
      ], []);
      const uniqueSources = new Map();

      sources.forEach((source) => {
        const key = source.id || `${source.type}-${JSON.stringify(source.metadata || {})}`;
        if (!uniqueSources.has(key)) {
          uniqueSources.set(key, {
            key,
            name: source.type || 'Fuente de verificación',
            description: source.metadata?.name
              || source.metadata?.full_address
              || [source.metadata?.state, source.metadata?.file_number].filter(Boolean).join(' · ')
              || 'Fuente incluida en el reporte de Middesk.',
            status: 'Consultada',
            color: '#00A342',
          });
        }
      });

      (business.registrations || []).forEach((registration) => {
        if (!registration.source) return;
        const key = `registration-${registration.id || registration.source}`;
        if (!uniqueSources.has(key)) {
          uniqueSources.set(key, {
            key,
            name: `${registration.state || 'SOS'} · Secretary of State`,
            description: [registration.name, registration.file_number].filter(Boolean).join(' · '),
            status: registration.status || 'Consultada',
            color: this.getMiddeskStatusColor(registration.status),
            url: registration.source,
          });
        }
      });

      return Array.from(uniqueSources.values());
    },
    buildMiddeskReportSections() {
      const business = this.middeskBusiness || {};
      const names = Array.isArray(business.names) ? business.names : [];
      const addresses = Array.isArray(business.addresses) ? business.addresses : [];
      const registrations = Array.isArray(business.registrations) ? business.registrations : [];
      const people = Array.isArray(business.people) ? business.people : [];
      const orders = Array.isArray(business.orders) ? business.orders : [];
      const watchlistLists = Array.isArray(business.watchlist?.lists) ? business.watchlist.lists : [];
      const buildRows = (items, mapper, emptyValue) => items.length
        ? items.map(mapper)
        : [{ label: emptyValue, value: '—' }];
      const sourceDescription = (sources = []) => sources
        .map((source) => source.metadata?.state || source.metadata?.name || source.type)
        .filter(Boolean)
        .join(', ');
      const productWasOrdered = (...products) => orders.some((order) => products.includes(order.product));
      const resultLabel = (items, ordered) => {
        if (!ordered) return 'No solicitado';
        return items.length ? `${items.length} encontrados` : 'Sin hallazgos';
      };
      const connectionTask = (business.review?.tasks || [])
        .find((task) => task.key === 'business_connections');

      return [
        {
          title: 'Nombres empresariales',
          status: names.length ? `${names.length} encontrados` : 'Sin variantes',
          color: names.length ? '#2163FF' : '#00A342',
          rows: buildRows(names, (name) => ({
            label: name.name || 'Nombre no disponible',
            value: name.submitted ? 'Presentado' : 'Encontrado',
            note: [name.type, sourceDescription(name.sources)].filter(Boolean).join(' · '),
          }), 'Sin nombres adicionales'),
        },
        {
          title: 'Direcciones',
          status: addresses.length ? `${addresses.length} encontradas` : 'Sin direcciones',
          color: addresses.length ? '#00A342' : '#667085',
          rows: buildRows(addresses, (address) => ({
            label: this.formatMiddeskAddress(address),
            value: address.rating?.risk_rating
              ? `Riesgo ${address.rating.risk_rating}`
              : (address.submitted ? 'Presentada' : 'Encontrada'),
            note: [
              address.submitted ? 'Presentada' : 'Encontrada',
              address.property_type,
              address.deliverable === true ? 'Entregable' : address.deliverable === false ? 'No entregable' : null,
              sourceDescription(address.sources),
            ].filter(Boolean).join(' · '),
            details: (address.rating?.indicators || []).map((indicator) => (
              `${indicator.name}: ${indicator.description || indicator.value || 'Sin detalle'}`
            )),
          }), 'Sin direcciones disponibles'),
        },
        {
          title: 'Registros de Secretary of State',
          status: registrations.length ? `${registrations.length} encontrados` : 'Sin registros',
          color: registrations.length ? '#00A342' : '#667085',
          rows: buildRows(registrations, (registration) => ({
            label: `${registration.state || 'Sin estado'} · ${registration.name || 'Registro empresarial'}`,
            value: [registration.status, registration.sub_status].filter(Boolean).join(' · ') || 'Estado no disponible',
            note: [
              registration.registration_date,
              registration.jurisdiction,
              registration.entity_type,
              registration.file_number,
            ].filter(Boolean).join(' · '),
            details: [
              registration.registered_agent?.name
                ? `Agente registrado: ${registration.registered_agent.name}` : null,
              ...(registration.officers || []).map((officer) => (
                `${officer.name}: ${(officer.roles || []).join(', ') || 'Sin cargo'}`
              )),
              ...(registration.addresses || []).map((address) => `Dirección: ${address}`),
            ].filter(Boolean),
            url: registration.source || '',
          }), 'Sin registros estatales disponibles'),
        },
        {
          title: 'Personas relacionadas',
          status: people.length ? `${people.length} encontradas` : 'Sin personas',
          color: people.length ? '#2163FF' : '#667085',
          rows: buildRows(people, (person) => ({
            label: person.name || [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(' ') || 'Nombre no disponible',
            value: person.submitted ? 'Presentada' : 'Encontrada',
            note: (person.titles || person.roles || [])
              .map((item) => (typeof item === 'string' ? item : item.title))
              .filter(Boolean)
              .join(', '),
            details: [
              person.sources?.length ? `${person.sources.length} fuentes` : null,
              person.people_bankruptcies?.length
                ? `${person.people_bankruptcies.length} bancarrotas relacionadas` : null,
            ].filter(Boolean),
          }), 'Sin personas relacionadas'),
        },
        {
          title: 'Watchlists',
          status: business.watchlist?.hit_count
            ? `${business.watchlist.hit_count} coincidencias`
            : 'Sin coincidencias',
          color: business.watchlist?.hit_count ? '#E60012' : '#00A342',
          rows: buildRows(watchlistLists, (list) => ({
            label: list.title || list.abbr || 'Lista de vigilancia',
            value: list.results?.length ? `${list.results.length} coincidencias` : 'Sin coincidencias',
            note: [list.agency, list.organization].filter(Boolean).join(' · '),
          }), 'Sin información de watchlists'),
        },
        {
          title: 'Conexiones',
          status: connectionTask?.sub_label || 'No evaluadas',
          color: this.getMiddeskStatusColor(connectionTask?.status),
          rows: [{
            label: connectionTask?.label || 'Conexiones empresariales',
            value: connectionTask?.sub_label || 'No evaluadas',
            note: connectionTask?.message || '',
          }],
        },
        {
          title: 'Gravámenes, litigios y bancarrotas',
          status: 'Resultados y cobertura',
          color: '#2163FF',
          rows: [
            {
              label: 'Gravámenes',
              value: resultLabel(business.liens || [], productWasOrdered('liens', 'ucc_liens', 'tax_liens')),
            },
            {
              label: 'Litigios',
              value: resultLabel(business.litigations || [], productWasOrdered('litigations')),
            },
            {
              label: 'Bancarrotas',
              value: resultLabel(business.bankruptcies || [], productWasOrdered('bankruptcies')),
            },
            {
              label: 'Personas políticamente expuestas',
              value: business.politically_exposed_person_screening?.status
                || (productWasOrdered('enhanced_screenings') ? 'Sin hallazgos' : 'No solicitado'),
            },
            {
              label: 'Medios adversos',
              value: business.adverse_media_screening?.status
                || (productWasOrdered('adverse_media') ? 'Sin hallazgos' : 'No solicitado'),
            },
          ],
        },
        {
          title: 'Productos solicitados',
          status: orders.length ? `${orders.length} órdenes` : 'Sin órdenes',
          color: orders.length ? '#2163FF' : '#667085',
          rows: buildRows(orders, (order) => ({
            label: order.product || order.package || 'Producto de Middesk',
            value: order.status || 'Sin estado',
            note: [order.package, order.completed_at].filter(Boolean).join(' · '),
            details: (order.subproducts || []).map((subproduct) => String(subproduct)),
          }), 'Sin productos registrados'),
        },
      ];
    },
    getMiddeskStatusColor(status) {
      const normalized = String(status || '').toLowerCase();
      if (['success', 'active', 'completed', 'approved'].includes(normalized)) return '#00A342';
      if (['failure', 'failed', 'inactive', 'rejected'].includes(normalized)) return '#E60012';
      if (['warning', 'unknown', 'in_review', 'pending'].includes(normalized)) return '#D97706';
      return '#667085';
    },
    sanitizeMiddeskResponse(value, key = '') {
      const normalizedKey = String(key).toLowerCase();
      const sensitiveKeys = [
        'tin', 'ein', 'ssn', 'social_security_number', 'taxpayer_identification_number',
        'email', 'email_address', 'email_addresses', 'phone', 'phone_number',
        'phone_numbers', 'dob', 'date_of_birth',
      ];
      if (sensitiveKeys.includes(normalizedKey) && value != null) return '[REDACTED]';
      if (normalizedKey === 'assignee' && value != null) return '[INTERNAL_DATA_REDACTED]';
      if (Array.isArray(value)) {
        return value.map((item) => this.sanitizeMiddeskResponse(item, key));
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).reduce((sanitized, childKey) => {
          sanitized[childKey] = this.sanitizeMiddeskResponse(value[childKey], childKey);
          return sanitized;
        }, {});
      }
      return value;
    },
    loadUsReportDemo() {
      this.queryType = 'company';
      this.companyCountry = 'US';
      this.queryStep = 3;
      this.usFlowStep = 'confirmation';
      this.usMockStatus = 'completed';
      this.showUsReport = true;
      this.activeUsReportTab = 'verification';
      this.selectedUsPackages = [
        'verify',
        'risk_analysis',
        'adverse_media',
        'enhanced_screenings',
        'ucc_tax',
        'business_litigation',
        'business_bankruptcy',
        'people_litigation',
        'people_bankruptcy',
        'people_tax_liens',
        'people_ucc_liens',
      ];
      this.usCompanyForm = {
        businessName: 'Acme Technologies Inc.',
        officeAddress: '1209 Orange Street, Wilmington, DE 19801',
        dbaName: 'Acme Technologies',
        tinEin: '22-2333444',
        formationState: 'DE',
        website: 'www.acmetechnologies.example',
        email: 'compliance@acmetechnologies.example',
      };
      this.usPeople = [
        {
          id: 1,
          firstName: 'John',
          middleName: 'A.',
          lastName: 'Smith',
          suffix: '',
          ssn: '123-45-6789',
          dateOfBirth: '1985-05-15',
          phoneNumber: '+12125550125',
        },
        {
          id: 2,
          firstName: 'Amanda',
          middleName: '',
          lastName: 'Johnson',
          suffix: '',
          ssn: '987-65-4321',
          dateOfBirth: '1990-01-01',
          phoneNumber: '+14155550150',
        },
      ];
    },
    selectQueryType(type) {
      this.queryType = type;
      this.companyCountry = '';
      this.error = null;
    },
    selectCompanyCountry(country) {
      this.companyCountry = country;
      this.usFlowStep = country === 'US' ? 'packages' : '';
      this.error = null;
    },
    resetCompanyCountry() {
      this.companyCountry = '';
      this.usFlowStep = 'packages';
      this.error = null;
    },
    isUsPackageSelected(packageValue) {
      return this.selectedUsPackages.includes(packageValue);
    },
    hasSelectedPeopleProducts() {
      return [
        'people_litigation',
        'people_bankruptcy',
        'people_tax_liens',
        'people_ucc_liens',
      ].some((product) => this.isUsPackageSelected(product));
    },
    getDigits(value) {
      return String(value || '').replace(/\D/g, '');
    },
    formatTinForDisplay(value) {
      const digits = this.getDigits(value);
      return digits.length === 9
        ? `${digits.slice(0, 2)}-${digits.slice(2)}`
        : 'No proporcionado';
    },
    maskSsn(value) {
      const digits = this.getDigits(value);
      return digits.length === 9
        ? `***-**-${digits.slice(-4)}`
        : 'No proporcionado';
    },
    getUsPersonName(person) {
      return [
        person.firstName,
        person.middleName,
        person.lastName,
        person.suffix,
      ].filter(Boolean).join(' ');
    },
    getUsStateName(value) {
      const state = this.usStates.find((item) => item.value === value);
      return state ? state.text : 'No proporcionado';
    },
    handleFormBack() {
      if (this.queryType !== 'company' || this.companyCountry === 'MX') {
        this.queryStep = 1;
        return;
      }
      const previousSteps = {
        business: 'packages',
        people: 'business',
        confirmation: 'people',
      };
      if (this.usFlowStep === 'packages') this.resetCompanyCountry();
      else this.usFlowStep = previousSteps[this.usFlowStep];
      this.error = null;
    },
    handleFormContinue() {
      if (this.queryType !== 'company' || this.companyCountry === 'MX') {
        this.handleStartAnalysis();
        return;
      }
      if (this.usFlowStep === 'packages') this.usFlowStep = 'business';
      else if (this.usFlowStep === 'business') this.continueUsBusiness();
      else if (this.usFlowStep === 'people') this.continueUsPeople();
      else this.startUsAnalysis();
    },
    continueUsBusiness() {
      this.error = null;
      if (!this.usCompanyForm.businessName || !this.usCompanyForm.officeAddress || !this.usCompanyForm.formationState) {
        this.error = 'Nombre, dirección y estado de constitución son obligatorios';
        return;
      }
      if (this.getDigits(this.usCompanyForm.tinEin).length !== 9) {
        this.error = 'El EIN/TIN es obligatorio y debe contener exactamente nueve dígitos';
        return;
      }
      if (this.isUsPackageSelected('risk_analysis') && !this.usCompanyForm.website) {
        this.error = 'El sitio web es obligatorio para realizar el análisis de riesgo';
        return;
      }
      if (this.usCompanyForm.website) {
        try {
          const website = /^https?:\/\//i.test(this.usCompanyForm.website)
            ? this.usCompanyForm.website
            : `https://${this.usCompanyForm.website}`;
          const parsedWebsite = new URL(website);
          if (!parsedWebsite.hostname) throw new Error();
        } catch (error) {
          this.error = 'El sitio web no tiene un formato válido';
          return;
        }
      }
      if (
        this.usCompanyForm.email
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.usCompanyForm.email)
      ) {
        this.error = 'El correo electrónico no tiene un formato válido';
        return;
      }
      this.usFlowStep = 'people';
    },
    continueUsPeople() {
      this.error = null;
      const enteredPeople = this.usPeople.filter((person) =>
        person.firstName
        || person.middleName
        || person.lastName
        || person.suffix
        || person.ssn
        || person.dateOfBirth
        || person.phoneNumber
      );
      const invalidPerson = enteredPeople.some(
        (person) => !person.firstName || !person.lastName
      );
      const invalidSsn = enteredPeople.some(
        (person) => person.ssn && this.getDigits(person.ssn).length !== 9
      );
      const invalidPhone = enteredPeople.some(
        (person) => person.phoneNumber
          && !/^\+[1-9]\d{7,14}$/.test(person.phoneNumber)
      );
      if (this.hasSelectedPeopleProducts() && !enteredPeople.length) {
        this.error = 'Debes agregar al menos una persona para los análisis personales';
        return;
      }
      if (invalidPerson) {
        this.error = 'El nombre y apellido de cada persona son obligatorios';
        return;
      }
      if (invalidSsn) {
        this.error = 'El SSN debe contener exactamente nueve dígitos';
        return;
      }
      if (
        this.isUsPackageSelected('people_bankruptcy')
        && enteredPeople.some((person) => this.getDigits(person.ssn).length !== 9)
      ) {
        this.error = 'El SSN completo es obligatorio para buscar bancarrotas personales';
        return;
      }
      if (invalidPhone) {
        this.error = 'El teléfono debe estar en formato internacional, por ejemplo +12125550125';
        return;
      }
      this.usFlowStep = 'confirmation';
    },
    addUsPerson() {
      this.usPeople.push({
        id: Date.now(),
        firstName: '',
        middleName: '',
        lastName: '',
        suffix: '',
        ssn: '',
        dateOfBirth: '',
        phoneNumber: '',
      });
    },
    removeUsPerson(index) {
      this.usPeople.splice(index, 1);
    },
    async startUsAnalysis() {
      if (this.loading) return;
      this.error = null;
      this.loading = true;
      this.usMockStatus = 'processing';
      this.activeUsReportTab = 'verification';

      if (!this.usIdempotencyKey) {
        this.usIdempotencyKey = this.createIdempotencyKey();
      }

      const people = this.usPeople
        .filter((person) => person.firstName && person.lastName)
        .map((person) => ({
          firstName: person.firstName,
          middleName: person.middleName,
          lastName: person.lastName,
          suffix: person.suffix,
          dateOfBirth: person.dateOfBirth,
          ssn: person.ssn,
          phoneNumber: person.phoneNumber,
        }));

      try {
        const response = await DB.post(
          `${URI}/api/v1/${this.selectLanguage}/middesk/consultations`,
          {
            idempotencyKey: this.usIdempotencyKey,
            businessName: this.usCompanyForm.businessName,
            officeAddress: this.usCompanyForm.officeAddress,
            dbaName: this.usCompanyForm.dbaName,
            tin: this.usCompanyForm.tinEin,
            formationState: this.usCompanyForm.formationState,
            website: this.usCompanyForm.website,
            email: this.usCompanyForm.email,
            people,
            selectedProducts: this.selectedUsPackages,
          },
          {
            headers: this.getAuthorizationHeaders(),
          }
        );

        this.queryStep = 3;
        const consultation = response.data.data;
        this.applyMiddeskConsultation(consultation);
        if (consultation?.id) {
          this.$router.replace({
            path: '/dueDiligence/new',
            query: {
              provider: 'middesk',
              consultationId: consultation.id,
            },
          }).catch(() => {});
        }
      } catch (error) {
        this.error = error.response?.data?.message
          || 'No se pudo registrar la consulta de Middesk';

        if (error.response
          && error.response.status < 500
          && error.response.status !== 409) {
          this.usIdempotencyKey = '';
        }
      } finally {
        this.loading = false;
      }
    },
    startAnotherQuery() {
      this.queryStep = 1;
      this.queryType = 'person';
      this.companyCountry = '';
      this.middeskConsultationId = '';
      this.middeskConsultation = null;
      this.usIdempotencyKey = '';
      this.middeskBusinessId = '';
      this.middeskBusiness = null;
      this.usFlowStep = 'packages';
      this.selectedUsPackages = ['verify'];
      this.usMockStatus = 'idle';
      this.showUsReport = false;
      this.activeUsReportTab = 'verification';
      this.error = null;
      this.usCompanyForm = {
        businessName: '',
        officeAddress: '',
        dbaName: '',
        tinEin: '',
        formationState: '',
        website: '',
        email: '',
      };
      this.usPeople = [];
    },
    handleCancel() {
      this.$router.push('/dueDiligence');
    },
    validatePersonForm() {
      if (!this.personForm.nombres || !this.personForm.apellidoPaterno || !this.personForm.fechaNacimiento) {
        this.error = 'Nombre(s), apellido paterno y fecha de nacimiento son obligatorios';
        return false;
      }
      if (!this.personForm.frenteIne || !this.personForm.reversoIne) {
        this.error = 'Debe adjuntar el INE por frente y reverso';
        return false;
      }
      return true;
    },
    validateCompanyForm() {
      if (!this.companyForm.razonSocial) {
        this.error = 'La razón social es obligatoria';
        return false;
      }
      return true;
    },
    async handleStartAnalysis() {
      this.error = null;

      const isValid = this.queryType === 'person' ? this.validatePersonForm() : this.validateCompanyForm();
      if (!isValid) return;

      this.queryStep = 3;
      this.loading = true;

      try {
        if (this.queryType === 'person') {
          await this.submitPerson();
        } else {
          await this.submitCompany();
        }

        this.mixSuccess('Consulta generada correctamente');
        this.$router.push('/dueDiligence');
      } catch (err) {
        this.error = err.response?.data?.message || 'Ocurrió un error al procesar la solicitud';
        this.mixError(this.error, err.response?.status);
        this.queryStep = 2;
      } finally {
        this.loading = false;
      }
    },
    submitPerson() {
      const formData = new FormData();
      formData.append('curp', this.personForm.curp);
      formData.append('nombres', this.personForm.nombres);
      formData.append('apellidoPaterno', this.personForm.apellidoPaterno);
      formData.append('apellidoMaterno', this.personForm.apellidoMaterno);
      formData.append('sexo', this.personForm.sexo);
      formData.append('telefono', this.personForm.telefono);
      formData.append('estadoNacimiento', this.personForm.estadoNacimiento);
      formData.append('fechaNacimiento', this.personForm.fechaNacimiento);
      formData.append('rfc', this.personForm.rfc);
      formData.append('correoElectronico', this.personForm.correoElectronico);
      formData.append('frenteIne', this.personForm.frenteIne);
      formData.append('reversoIne', this.personForm.reversoIne);

      return DB.post(`${URI}/api/v1/${this.selectLanguage}/check/persona/consultar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: this.$store.state.sToken ? `Bearer ${this.$store.state.sToken}` : '',
        },
      });
    },
    submitCompany() {
      const formData = new FormData();
      formData.append('razonSocial', this.companyForm.razonSocial);
      formData.append('rfc', this.companyForm.rfc);
      formData.append('marca', this.companyForm.marca);
      formData.append('dominio', this.companyForm.dominio);
      if (this.companyForm.constanciaFiscal) {
        formData.append('constanciaFiscal', this.companyForm.constanciaFiscal);
      }

      return DB.post(`${URI}/api/v1/${this.selectLanguage}/check/empresa/consultar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: this.$store.state.sToken ? `Bearer ${this.$store.state.sToken}` : '',
        },
      });
    },
  },
};
</script>

<style scoped>
.identity-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.query-flow {
  max-width: 1220px;
  min-height: calc(100vh - 64px);
  margin: 0 auto;
  padding-top: 20px;
}

.flow-stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.step-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #e1e4ea;
  color: #6b7280;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 54px;
  width: 16px;
  height: 4px;
  background: #e1e4ea;
}

.step-item.active {
  background: #1876d2;
  color: white;
}

.step-item.completed:not(:last-child)::after {
  background: #1876d2;
}

.step-label {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  text-align: center;
}

.flow-type-section {
  text-align: center;
}

.flow-type-section h2 {
  font-family: 'montserrat-bold';
  font-size: 40px;
  margin-bottom: 18px;
}

.flow-type-section > p {
  font-family: 'montserrat-semibold';
  font-size: 23px;
  color: #465266;
  margin-bottom: 48px;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
}

.type-card {
  min-height: 318px;
  padding: 42px;
  border: 2px solid #dfe3ea;
  border-radius: 18px;
  background: white;
  text-align: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.type-card.selected {
  border-color: #1876d2;
  background: #f7fbff;
  box-shadow: 0 6px 14px rgba(24, 118, 210, 0.14);
}

.type-card.selected .type-icon {
  background: #eaf4fd;
}

.type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 106px;
  height: 106px;
  margin: 14px auto 28px;
  border-radius: 18px;
  background: #f0f1f5;
}

.type-card strong {
  display: block;
  margin-bottom: 14px;
  font-family: 'montserrat-bold';
  font-size: 27px;
  color: #0c0d10;
}

.type-card span {
  display: block;
  max-width: 430px;
  margin: 0 auto;
  font-family: 'montserrat-bold';
  font-size: 18px;
  line-height: 1.45;
  color: #465266;
}

.flow-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 44px;
}

.flow-secondary-button,
.flow-primary-button,
.document-button {
  height: 48px !important;
  border-radius: 8px;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.flow-secondary-button {
  min-width: 124px;
  border-color: #d8d9df;
  background: white;
  color: #111111;
}

.flow-primary-button {
  min-width: 160px;
  background: #1876d2 !important;
  color: white !important;
}

.flow-primary-button.v-btn--disabled {
  background: #d9e0e8 !important;
  color: #8a96a8 !important;
}

.form-card,
.processing-card {
  border-radius: 16px;
  background: white;
}

.form-card h2 {
  font-family: 'montserrat-bold';
  font-size: 32px;
  margin-bottom: 14px;
}

.form-card > p {
  margin-bottom: 32px;
  font-family: 'montserrat-semibold';
  font-size: 21px;
  color: #465266;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px 20px;
}

.form-grid label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'montserrat-bold';
  font-size: 18px;
  color: #0c0d10;
}

.form-grid label.full-width,
.documents-box {
  grid-column: 1 / -1;
}

.form-grid input,
.form-grid select {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 0;
  border-radius: 9px;
  outline: none;
  background: #f0f0f3;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #111827;
}

.form-grid input::placeholder,
.form-grid select {
  color: #717789;
}

.documents-box {
  padding: 22px;
  border: 1px solid #9fc7ff;
  border-radius: 10px;
  background: #edf6ff;
}

.documents-box strong {
  display: block;
  margin-bottom: 12px;
  font-family: 'montserrat-bold';
  font-size: 18px;
  color: #0c4de8;
}

.documents-box p {
  margin-bottom: 18px;
  font-family: 'montserrat-semibold';
  font-size: 16px;
  color: #0c4de8;
}

.document-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-footer {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 44px;
  padding-top: 32px;
  border-top: 1px solid #e3e5ea;
}

.text-action {
  padding: 0;
  border: 0;
  background: transparent;
  color: #1876d2;
  font-family: 'montserrat-semibold';
  font-size: 15px;
  cursor: pointer;
}

.us-flow {
  width: 100%;
}

.package-group,
.associated-person,
.review-card,
.report-section {
  margin-bottom: 24px;
  padding: 24px;
  border: 1px solid #dfe3ea;
  border-radius: 14px;
  background: white;
}

.package-group h3,
.associated-person h3,
.review-card h3,
.report-section h3 {
  font-family: 'montserrat-bold';
  font-size: 20px;
}

.package-option {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 14px;
  padding: 20px;
  border: 1px solid #dfe3ea;
  border-radius: 12px;
  cursor: pointer;
}

.package-option.selected {
  border-color: #1876d2;
  background: #f3f8fd;
}

.package-option input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: #1876d2;
}

.package-option span,
.package-option small,
.review-card span {
  display: block;
}

.package-option strong {
  font-family: 'montserrat-bold';
  font-size: 17px;
}

.package-option small,
.review-card span {
  margin-top: 7px;
  color: #465266;
  font-family: 'montserrat-semibold';
}

.confirmation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 28px;
  margin-top: 24px;
}

.confirmation-grid.compact {
  gap: 16px 24px;
  margin-top: 16px;
}

.confirmation-field {
  min-width: 0;
}

.confirmation-field.full-width {
  grid-column: 1 / -1;
}

.confirmation-field span {
  margin: 0 0 6px;
  color: #7a869a;
  font-family: 'montserrat-semibold';
  font-size: 13px;
}

.confirmation-field strong {
  display: block;
  overflow-wrap: anywhere;
  color: #15171a;
  font-family: 'montserrat-semibold';
  font-size: 15px;
  line-height: 1.45;
}

.confirmation-empty {
  margin: 16px 0 0;
  color: #667085;
  font-family: 'montserrat-semibold';
}

.confirmation-person {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e5e8ee;
}

.confirmation-person > strong {
  font-family: 'montserrat-bold';
  font-size: 16px;
}

.associated-person-header,
.report-header,
.report-section-title,
.report-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.associated-person-header {
  margin-bottom: 20px;
}

.us-report {
  padding-bottom: 40px;
}

.us-report-heading,
.us-report-actions,
.provider-card-header,
.source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.us-report-heading {
  margin-bottom: 24px;
}

.us-report-heading h1 {
  margin: 5px 0;
  font-family: 'montserrat-bold';
  font-size: 34px;
}

.us-report-heading p,
.provider-card-header p {
  margin: 4px 0 0;
  color: #667085;
}

.us-report-tabs {
  display: flex;
  gap: 30px;
  margin-bottom: 26px;
  border-bottom: 1px solid #dfe3ea;
  overflow-x: auto;
}

.us-report-tabs button {
  padding: 14px 0;
  border: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #465266;
  font-family: 'montserrat-bold';
  white-space: nowrap;
  cursor: pointer;
}

.us-report-tabs button.active {
  border-color: #123f4a;
  color: #123f4a;
}

.us-report-layout {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.us-summary-panel,
.provider-card {
  border: 1px solid #dfe3ea;
  border-radius: 14px;
  background: white;
}

.us-summary-panel {
  position: sticky;
  top: 20px;
  overflow: hidden;
}

.us-summary-panel h2,
.provider-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e7e9ee;
}

.us-summary-panel h2,
.provider-card-header h2 {
  margin: 0;
  font-family: 'montserrat-bold';
  font-size: 21px;
}

.us-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 17px 20px;
  border-bottom: 1px solid #edf0f4;
}

.us-summary-row span {
  color: #667085;
}

.us-summary-row strong {
  display: flex;
  align-items: center;
  gap: 5px;
  text-align: right;
}

.us-summary-state--success,
.status-success {
  color: #008a38;
}

.us-summary-state--warning,
.status-warning {
  color: #d97706;
}

.us-summary-state--error,
.status-error {
  color: #e60012;
}

.error-state-icon {
  background: #e60012;
}

.provider-card {
  margin-bottom: 24px;
  overflow: hidden;
}

.provider-card-header > div:last-child {
  text-align: right;
}

.last-updated {
  display: block;
  margin-top: 8px;
  color: #667085;
}

.business-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  padding: 26px;
}

.business-detail-grid span,
.business-detail-grid strong,
.business-detail-grid small {
  display: block;
}

.business-detail-grid span {
  margin-bottom: 7px;
  color: #667085;
}

.business-detail-grid small {
  margin-top: 7px;
  font-family: 'montserrat-semibold';
}

.provider-table {
  padding: 0 24px;
}

.provider-table-row {
  display: grid;
  grid-template-columns: minmax(180px, 0.8fr) minmax(240px, 1.5fr) minmax(120px, 0.8fr);
  gap: 22px;
  padding: 18px 0;
  border-bottom: 1px solid #edf0f4;
}

.provider-table-row:last-child,
.source-row:last-child {
  border-bottom: 0;
}

.provider-table-row span,
.provider-table-row small,
.source-row span {
  color: #667085;
}

.source-row {
  padding: 20px 0;
  border-bottom: 1px solid #edf0f4;
}

.source-row strong,
.source-row span {
  display: block;
}

.source-row span {
  margin-top: 5px;
}

.api-response {
  max-height: 520px;
  margin: 24px;
  padding: 24px;
  border-radius: 10px;
  overflow: auto;
  background: #111827;
  color: #d9fae4;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.report-header {
  padding: 28px;
  border-radius: 16px;
  background: white;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.report-header h2 {
  margin: 5px 0;
  font-family: 'montserrat-bold';
  font-size: 30px;
}

.report-header p {
  margin: 0;
  color: #465266;
}

.report-eyebrow {
  color: #1876d2;
  font-family: 'montserrat-bold';
  font-size: 14px;
  text-transform: uppercase;
}

.report-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.summary-item {
  padding: 20px;
  border: 1px solid #dfe3ea;
  border-radius: 12px;
  background: white;
}

.summary-item span,
.summary-item strong {
  display: block;
}

.summary-item span {
  margin-bottom: 8px;
  color: #465266;
}

.summary-item .success {
  color: #008a38;
}

.summary-item .warning {
  color: #d97706;
}

.report-section-title {
  margin-bottom: 18px;
}

.report-row {
  padding: 15px 0;
  border-top: 1px solid #edf0f4;
}

.processing-card {
  min-height: 492px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.processing-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 108px;
  height: 108px;
  margin-bottom: 64px;
  border-radius: 50%;
  background: #1876d2;
}

.processing-card h2 {
  font-family: 'montserrat-bold';
  font-size: 32px;
  margin-bottom: 42px;
}

.processing-card p {
  font-family: 'montserrat-semibold';
  font-size: 21px;
  color: #465266;
}

.identity-page.us-report-page {
  max-width: none;
}

.us-report-page .query-flow {
  max-width: 1680px;
  padding-top: 0;
}

.us-report {
  font-family: 'montserrat-regular';
  font-size: 14px;
  line-height: 1.5;
  color: #383e43;
}

.us-report-heading h1 {
  font-family: inherit;
  font-size: 26px;
  font-weight: 500;
}

.us-report-tabs button {
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
}

.us-report-tabs button.active {
  font-weight: 600;
}

.us-report-layout {
  grid-template-columns: minmax(260px, 22%) minmax(0, 1fr);
}

.us-summary-panel {
  border-radius: 4px;
  position: static;
}

.us-summary-panel h2 {
  padding: 14px 20px;
  font-family: 'montserrat-semibold';
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}

.us-summary-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 6px 12px;
  min-height: 66px;
  margin: 0 18px;
  padding: 16px 0;
  font-size: 13px;
}

.us-summary-row:last-child {
  border-bottom: 0;
}

.us-summary-row strong {
  font-weight: 400;
  justify-content: flex-end;
  overflow-wrap: anywhere;
  color: #383e43;
}

.us-summary-row strong .v-icon {
  order: 1;
  flex-shrink: 0;
}

.us-summary-explanation {
  grid-column: 1 / -1;
  color: #667085;
  font-size: 12px;
}

.us-summary-explanation summary {
  cursor: pointer;
  width: fit-content;
}

.us-summary-explanation summary:focus-visible {
  outline: 2px solid #123f4a;
  outline-offset: 3px;
}

.us-summary-explanation p {
  margin: 8px 0 0;
  overflow-wrap: anywhere;
}

.us-business-card {
  border-radius: 10px;
}

.us-business-card .provider-card-header {
  padding: 14px 24px;
  min-height: 62px;
}

.us-business-card .provider-card-header h2 {
  font-family: inherit;
  font-size: 20px;
  font-weight: 400;
}

.us-business-card .v-btn {
  text-transform: none;
  letter-spacing: normal;
}

.us-business-card .business-detail-grid {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr) minmax(0, 1fr);
  gap: 26px 30px;
  padding: 28px 24px 32px;
}

.us-business-card .business-detail-grid span {
  font-size: 12px;
  margin-bottom: 3px;
}

.us-business-card .business-detail-grid strong {
  font-weight: 400;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.us-business-card .business-detail-grid .us-detail-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 13px;
  margin-top: 4px;
}

@media (max-width: 1200px) {
  .us-business-card .business-detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .us-business-card .business-detail-grid {
    grid-template-columns: 1fr;
  }
  .identity-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .step-item:not(:last-child)::after {
    width: 16px;
    left: 54px;
  }

  .flow-type-section h2 {
    font-size: 32px;
  }

  .flow-type-section > p {
    font-size: 18px;
  }

  .type-grid,
  .form-grid,
  .confirmation-grid,
  .document-actions,
  .report-summary {
    grid-template-columns: 1fr;
  }

  .report-header,
  .report-section-title,
  .report-row,
  .us-report-heading,
  .provider-card-header,
  .source-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .us-report-actions {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
  }

  .us-report-layout,
  .business-detail-grid,
  .provider-table-row {
    grid-template-columns: 1fr;
  }

  .us-summary-panel {
    position: static;
  }

  .provider-card-header > div:last-child {
    text-align: left;
  }

  .type-card {
    min-height: auto;
    padding: 28px;
  }

  .flow-footer,
  .form-footer {
    flex-direction: column;
  }

  .flow-secondary-button,
  .flow-primary-button {
    width: 100%;
  }

  .form-card h2 {
    font-size: 28px;
  }

  .form-card > p {
    font-size: 18px;
  }

  .processing-card {
    min-height: 420px;
  }
}
.provider-row-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: 1 / -1;
  margin-top: 6px;
  color: #667085;
  font-size: 12px;
}

.provider-table-row > a,
.source-row a {
  color: #2163ff;
  font-size: 12px;
  text-decoration: none;
}

.provider-table-row > a:hover,
.source-row a:hover {
  text-decoration: underline;
}

/* Fresher AI shared visual language */
.identity-page {
  width: 100%;
  max-width: 1740px !important;
  margin: 0 auto;
  padding: 28px 40px 32px !important;
  font-family: 'montserrat-regular';
}

.identity-page.us-report-page {
  max-width: 1740px !important;
}

.query-flow {
  padding-top: 0;
}

.flow-type-section h2 {
  font-size: 30px;
  line-height: 1.25;
}

.flow-type-section > p {
  font-family: 'montserrat-regular';
  font-size: 15px;
  color: #718096;
}

.type-card,
.form-card,
.processing-card,
.us-summary-panel,
.us-business-card,
.provider-card {
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.type-card {
  min-height: 260px;
  padding: 30px;
}

.form-card h2 {
  font-size: 26px;
}

.form-card > p,
.processing-card p {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.flow-primary-button {
  background: #1876d2 !important;
}

.us-report-heading h1 {
  font-family: 'montserrat-semibold';
  font-size: 26px;
}

.us-report-tabs button,
.us-business-card .v-btn,
.provider-card .v-btn {
  font-family: 'montserrat-semibold';
}

@media (max-width: 960px) {
  .identity-page {
    padding: 24px !important;
  }

  .flow-type-section h2 {
    font-size: 26px;
  }

  .flow-type-section > p {
    font-size: 14px;
  }
}

@media (max-width: 600px) {
  .identity-page {
    padding: 20px 16px 28px !important;
  }

  .type-card,
  .form-card,
  .processing-card {
    padding: 22px 18px !important;
  }
}

</style>
